import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Minus, Plus, Loader2, ShieldCheck, CalendarDays, Clock, Users, Ticket, Flame, Ban } from "lucide-react";
import { toast } from "sonner";
import { PrimaryButton, openEnquiry } from "./common";
import { CRUISE_DATES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADULT = 49;
const CHILD = 35;
const SELLING_FAST = 40;

// Must exactly match the cruise label built at checkout and stored on the server.
const cruiseLabel = (d, t) => `${d.weekday} ${d.day} ${d.month} 2027 · ${t}`;

const Stepper = ({ label, price, value, setValue, testid, disabled }) => (
  <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-4">
    <div>
      <p className="font-medium text-white">{label}</p>
      <p className="text-sm text-white/60">${price} AUD each</p>
    </div>
    <div className="flex items-center gap-3">
      <button
        data-testid={`${testid}-minus`}
        onClick={() => setValue(Math.max(0, value - 1))}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition hover:bg-white/10 disabled:opacity-40"
        aria-label={`Decrease ${label}`}
      >
        <Minus className="h-4 w-4" />
      </button>
      <span data-testid={`${testid}-count`} className="w-6 text-center text-lg font-semibold">{value}</span>
      <button
        data-testid={`${testid}-plus`}
        onClick={() => setValue(value + 1)}
        disabled={disabled}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-vivid-magenta text-white transition hover:bg-vivid-magentaHover disabled:cursor-not-allowed disabled:opacity-40"
        aria-label={`Increase ${label}`}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const field = "w-full rounded-lg border border-white/10 bg-vivid-base/60 px-3 py-2.5 text-white outline-none focus:border-vivid-magenta";

const BookingWidget = ({ prefix = "", showCharterLink = true }) => {
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [dateIdx, setDateIdx] = useState(0);
  const selectedDate = CRUISE_DATES[dateIdx];
  const [time, setTime] = useState(selectedDate.times[0]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [capacity, setCapacity] = useState(100);
  const [sold, setSold] = useState({});

  const loadAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/availability`);
      setCapacity(res.data?.capacity || 100);
      setSold(res.data?.sold || {});
    } catch (e) {
      // Non fatal: if availability cannot load, allow booking normally.
      console.error("availability load failed", e);
    }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const remainingFor = useCallback(
    (d, t) => Math.max(0, capacity - (sold[cruiseLabel(d, t)] || 0)),
    [capacity, sold]
  );

  const dateSoldOut = (d) => d.times.every((t) => remainingFor(d, t) <= 0);
  const remaining = remainingFor(selectedDate, time);
  const total = adults * ADULT + children * CHILD;
  const totalTickets = adults + children;

  const onDateChange = (e) => {
    const idx = Number(e.target.value);
    const d = CRUISE_DATES[idx];
    setDateIdx(idx);
    const firstOpen = d.times.find((t) => remainingFor(d, t) > 0) || d.times[0];
    setTime(firstOpen);
  };

  const handleCheckout = async () => {
    if (totalTickets === 0) {
      toast.error("Please add at least one ticket.");
      return;
    }
    if (remaining <= 0) {
      toast.error("This cruise is sold out. Please choose another date or time.");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email so we can send your confirmation.");
      return;
    }
    setLoading(true);
    try {
      const label = cruiseLabel(selectedDate, time);
      const origin = window.location.origin + window.location.pathname;
      const res = await axios.post(`${API}/checkout/session`, {
        adults,
        children,
        origin_url: origin,
        cruise_date: label,
        customer_name: name,
        customer_email: email,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.status === 409
        ? (e.response.data?.detail || "This cruise is sold out.")
        : "Could not start checkout. Please try again or send an enquiry.";
      toast.error(msg);
      loadAvailability();
      setLoading(false);
    }
  };

  const soldOut = remaining <= 0;
  const sellingFast = !soldOut && remaining <= SELLING_FAST;

  return (
    <div className="relative rounded-3xl border border-vivid-magenta/30 bg-vivid-surface/70 p-6 shadow-[0_0_40px_rgba(255,0,122,0.15)] sm:p-10">
      <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-b from-vivid-magenta/20 to-transparent opacity-40" />
      <div className="relative">
        <div className="mb-6 flex items-center gap-2 text-vivid-cyan">
          <Ticket className="h-5 w-5" />
          <span className="text-sm uppercase tracking-[0.2em]">Book Vivid Cruises Sydney</span>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <label htmlFor={`${prefix}date-select`} className="mb-2 flex items-center gap-2 text-sm text-white/70">
                <CalendarDays className="h-4 w-4" /> Cruise date
              </label>
              <select id={`${prefix}date-select`} data-testid={`${prefix}date-select`} value={dateIdx} onChange={onDateChange} className={field}>
                {CRUISE_DATES.map((d, i) => (
                  <option key={d.iso} value={i} className="bg-vivid-surface" disabled={dateSoldOut(d)}>
                    {d.weekday} {d.day} {d.month}{d.finale ? " · Finale" : ""}{dateSoldOut(d) ? " · Sold out" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <label htmlFor={`${prefix}time-select`} className="mb-2 flex items-center gap-2 text-sm text-white/70">
                <Clock className="h-4 w-4" /> Departure time
              </label>
              <select id={`${prefix}time-select`} data-testid={`${prefix}time-select`} value={time} onChange={(e) => setTime(e.target.value)} className={field}>
                {selectedDate.times.map((t) => {
                  const r = remainingFor(selectedDate, t);
                  return (
                    <option key={t} value={t} className="bg-vivid-surface" disabled={r <= 0}>
                      {t}{r <= 0 ? " · Sold out" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Urgency messaging only. Exact seat counts stay private in the admin dashboard. */}
          {soldOut ? (
            <div data-testid={`${prefix}availability`} className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm font-semibold text-red-400">
              <Ban className="h-4 w-4" /> This cruise is sold out. Please choose another date or time.
            </div>
          ) : sellingFast ? (
            <div data-testid={`${prefix}availability`} className="flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm font-semibold text-amber-400">
              <Flame className="h-4 w-4" /> Hurry, tickets are selling fast for this cruise!
            </div>
          ) : null}

          <Stepper label="Adults" price={ADULT} value={adults} setValue={setAdults} testid={`${prefix}adults`} disabled={soldOut} />
          <Stepper label="Children" price={CHILD} value={children} setValue={setChildren} testid={`${prefix}children`} disabled={soldOut} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid={`${prefix}customer-name`}
              type="text"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={field + " placeholder:text-white/40"}
            />
            <input
              data-testid={`${prefix}customer-email`}
              type="email"
              placeholder="Email for confirmation*"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={field + " placeholder:text-white/40"}
            />
          </div>
        </div>

        <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-6">
          <div>
            <p className="text-sm text-white/60">Total ({totalTickets} tickets)</p>
            <p data-testid={`${prefix}ticket-total`} className="font-heading text-4xl font-bold text-white">${total}<span className="text-lg text-white/50"> AUD</span></p>
          </div>
          <PrimaryButton data-testid={`${prefix}checkout-btn`} onClick={handleCheckout} disabled={loading || soldOut}>
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting…</> : soldOut ? "Sold out" : "Book & Pay"}
          </PrimaryButton>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
          <ShieldCheck className="h-4 w-4 text-vivid-cyan" /> Secure payment via Stripe · Instant confirmation
        </div>

        {showCharterLink && (
          <button
            data-testid={`${prefix}private-charter-enquiry-btn`}
            onClick={() => openEnquiry("Private Charter")}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm text-white/80 transition hover:bg-white/5"
          >
            <Users className="h-4 w-4" /> Enquire about private charters & group rates
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingWidget;
